import { Maximize2, Minimize2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import PlaybackSpeed from './PlayBackSpeed';
// play-pause, volume, forward backward button icons from react-icons
import { FaBackward, FaForward, FaPause, FaPlay, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import './WayPlayer.css';

interface VideoPlayerProps {
  videoId: string;
  startTime?: number;
  options?: {
    poster?: string;
  };
}

const WayPlayer: React.FC<VideoPlayerProps> = ({ videoId, startTime = 0 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const playerRef = useRef<YT.Player | null>(null);
  const progressInterval = useRef<number>();
  const clickTimeoutRef = useRef<number>();
  const clickCountRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number }>({ visible: false, x: 0, y: 0 });

  // Load YouTube IFrame API once
  useEffect(() => {
    if (window.YT) {
      setIsAPIReady(true);
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
    };
  }, []);

  // Add styles to disable YouTube controls
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ytp-chrome-top,
      .ytp-chrome-bottom,
      .ytp-watermark,
      .ytp-show-cards-title,
      .ytp-pause-overlay,
      .ytp-youtube-button,
      .ytp-embed,
      iframe[src*="youtube.com"] .ytp-chrome-top,
      iframe[src*="youtube.com"] .ytp-chrome-bottom,
      .ytp-gradient-top,
      .ytp-gradient-bottom {
        display: none !important;
      }
      iframe {
        pointer-events: none !important;
      }
      .video-wrapper {
        user-select: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Initialize player when API is ready and videoId changes
  useEffect(() => {
    if (!isAPIReady) return;

    // Clean up existing player
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }

    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    // Initialize new player with new video ID
    if (!containerRef.current) return;

    playerRef.current = new YT.Player(containerRef.current, {
      width: '100%',
      height: '100%',
      videoId,
      playerVars: {
        autoplay: 0,
        controls: 0,
        modestbranding: 1,
        loop: 1,
        playlist: videoId,
        playsinline: 1,
        rel: 0,
        showinfo: 0,
        mute: 0,
        enablejsapi: 1
      },
      events: {
        onReady: (event: any) => {
          event.target.pauseVideo();
          event.target.unMute();
          event.target.setVolume(volume);
          setDuration(event.target.getDuration());

          // Seek to start time if provided and not 0
          if (startTime && startTime > 0) {
            event.target.seekTo(startTime, true);
          }

          // Start progress tracking
          progressInterval.current = window.setInterval(() => {
            if (playerRef.current) {
              const currentTime = playerRef.current.getCurrentTime();
              const duration = playerRef.current.getDuration();
              setProgress((currentTime / duration) * 100);
            }
          }, 1000);
        },
        onStateChange: (event) => {
          setIsPlaying(event.data === YT.PlayerState.PLAYING);
        }
      }
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [videoId, isAPIReady, startTime, volume]);

  // Sync volume changes with player
  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.setVolume(volume);
      if (volume > 0 && isMuted) {
        playerRef.current.unMute();
        setIsMuted(false);
      }
    }
  }, [volume, isMuted]);

  // Keyboard controls
  useEffect(() => {
    // const handleKeyPress = (e: KeyboardEvent) => {
    //   if (e.target instanceof HTMLInputElement) return;

    //   switch (e.key.toLowerCase()) {
    //     case ' ':
    //     case 'k':
    //       e.preventDefault();
    //       togglePlay();
    //       break;
    //     case 'j':
    //       e.preventDefault();
    //       seekRelative(-10);
    //       break;
    //     case 'l':
    //       e.preventDefault();
    //       seekRelative(10);
    //       break;
    //     // case 'm':
    //     //   e.preventDefault();
    //     //   toggleMute();
    //     //   break;
    //     case 'arrowleft':
    //       e.preventDefault();
    //       seekRelative(-5);
    //       break;
    //     case 'arrowright':
    //       e.preventDefault();
    //       seekRelative(5);
    //       break;
    //     case 'arrowup':
    //       e.preventDefault();
    //       adjustVolume(10);
    //       break;
    //     case 'arrowdown':
    //       e.preventDefault();
    //       adjustVolume(-10);
    //       break;
    //     case 'f':
    //       e.preventDefault();
    //       toggleFullscreen();
    //       break;
    //   }
    // };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;

      const actions: { [key: string]: () => void } = {
        ' ': togglePlay,
        k: togglePlay,
        j: () => seekRelative(-10),
        l: () => seekRelative(10),
        arrowleft: () => seekRelative(-5),
        arrowright: () => seekRelative(5),
        arrowup: () => adjustVolume(10),
        arrowdown: () => adjustVolume(-10),
        f: toggleFullscreen,
        m: toggleMute,
      };

      const action = actions[e.key.toLowerCase()];
      if (action) {
        e.preventDefault();
        action();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  // const togglePlay = () => {
  //   if (!playerRef.current) return;
  //   if (isPlaying) {
  //     playerRef.current.pauseVideo();
  //   } else {
  //     playerRef.current.playVideo();
  //   }
  //   setIsPlaying(!isPlaying);
  // };

  const togglePlay = () => {
    if (playerRef.current) {
      const isCurrentlyPlaying = playerRef.current.getPlayerState() === YT.PlayerState.PLAYING;
      isCurrentlyPlaying ? playerRef.current.pauseVideo() : playerRef.current.playVideo();
      setIsPlaying(!isCurrentlyPlaying);
    }
  };

  // const toggleMute = () => {
  //   if (!playerRef.current) return;
  //   if (isMuted) {
  //     playerRef.current.unMute();
  //     playerRef.current.setVolume(volume);
  //   } else {
  //     playerRef.current.mute();
  //     playerRef.current.setVolume(0);
  //   }
  //   setIsMuted(!isMuted);
  // };

  const toggleMute = () => {
    if (playerRef.current) {
      if (isMuted) {
        playerRef.current.unMute();
        playerRef.current.setVolume(100);
        setVolume(100);
      } else {
        playerRef.current.setVolume(0);
        setVolume(0);
        playerRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value, 10);
    setVolume(newVolume);
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const handleVolumeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const adjustVolume = (delta: number) => {
    if (!playerRef.current) return;

    // Calculate new volume
    const newVolume = Math.max(0, Math.min(100, volume + delta));

    // Update the player volume
    playerRef.current.setVolume(newVolume);
    setVolume(newVolume);

    // Handle unmuting if volume is increased from 0
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
      playerRef.current.unMute();
    }
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!playerRef.current) return;
    const newProgress = parseFloat(e.target.value);
    const newTime = (newProgress / 100) * duration;

    // Update progress immediately for smoother UI feedback
    setProgress(newProgress);

    // Use requestAnimationFrame to debounce seeking
    requestAnimationFrame(() => {
      playerRef.current?.seekTo(newTime, true);
    });
  };

  const seekRelative = (seconds: number) => {
    if (!playerRef.current) return;
    const currentTime = playerRef.current.getCurrentTime();
    playerRef.current.seekTo(currentTime + seconds, true);
  };

  const handlePlaybackRateChange = (rate: number) => {
    if (!playerRef.current) return;
    playerRef.current.setPlaybackRate(rate);
    setPlaybackRate(rate);
  };

  const toggleFullscreen = () => {
    if (!wrapperRef.current) return;

    if (!document.fullscreenElement) {
      wrapperRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.log(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.log(`Error attempting to exit fullscreen: ${err.message}`);
      });
    }
  };

  const handleVideoClick = (e: React.MouseEvent<HTMLDivElement>) => {
    clickCountRef.current += 1;

    if (clickTimeoutRef.current) {
      window.clearTimeout(clickTimeoutRef.current);
    }

    if (contextMenu.visible) {
      handleContextMenuClose();
    }

    clickTimeoutRef.current = window.setTimeout(() => {
      if (clickCountRef.current === 1) {
        console.log('Single click');
        // Single click - toggle play/pause
        togglePlay();
      } else if (clickCountRef.current === 2) {
        console.log('Double click');
        // Double click - seek forward/backward
        const rect = wrapperRef.current?.getBoundingClientRect();
        console.log(rect);

        if (!rect) {
          console.error('getBoundingClientRect() failed: rect is undefined or null');
          return;
        }

        const clickX = e.clientX - rect.left;
        const isLeftSide = clickX < rect.width / 2;
        seekRelative(isLeftSide ? -10 : 10);
      }
      clickCountRef.current = 0;
    }, 250); // Adjust the timeout for double-click detection
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    setContextMenu({ visible: true, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleContextMenuClose = () => {
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    } else {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  };


  return (
    <div ref={wrapperRef} className="relative group  custom-height sm:rounded-lg">
      <div
        className="w-full aspect-video bg-black cursor-pointer video-wrapper"
        onClick={handleVideoClick}
        onContextMenu={handleContextMenu}
      >
        <div ref={containerRef} className="w-full h-full" />
      </div>

      {/* Custom Controls - Modified for mobile */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
        {/* Progress Bar */}
        <div className="mb-2">
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleProgressChange}
            className="w-full h-2 accent-white bg-white/30 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Controls with centered timestamp */}
        <div className="grid grid-cols-3 items-center mx-3">
          {/* Left controls */}
          <div className="flex items-center gap-6">
            <button
              onClick={togglePlay}
              className="text-white hover:text-gray-300 transition-colors"
            >
              {isPlaying ? <FaPause className="w-10 h-10" /> : <FaPlay className="w-10 h-10" />}
            </button>

            <button
              onClick={() => seekRelative(-10)}
              className="hidden sm:flex text-white hover:text-gray-300 transition-colors"
            >
              <FaBackward className="w-10 h-10" />
            </button>

            <button
              onClick={() => seekRelative(10)}
              className="hidden sm:flex text-white hover:text-gray-300 transition-colors"
            >
              <FaForward className="w-10 h-10" />
            </button>

            {/* Volume Controls */}
            <div
              className="flex items-center gap-2"
              onClick={e => e.stopPropagation()}
              onMouseDown={handleVolumeMouseDown}
            >
              <button
                onClick={toggleMute}
                className="text-white hover:text-gray-300 transition-colors"
              >
                {volume === 0 ? <FaVolumeMute className="w-10 h-10" /> : <FaVolumeUp className="w-10 h-10" />}
              </button>

              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                onClick={e => e.stopPropagation()}
                className="w-24 accent-white hidden sm:block"
              />
            </div>
          </div>

          {/* Center - Timestamp */}
          <div className="flex justify-center items-center text-white text-2xl">
            <span>{formatTime(duration * (progress / 100))} / {formatTime(duration)}</span>
          </div>

          {/* Right controls */}
          <div className="flex items-center justify-end gap-6">
            <div className='flex'>
              <PlaybackSpeed currentSpeed={playbackRate} onSpeedChange={handlePlaybackRateChange} />
            </div>

            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-gray-300 transition-colors"
            >
              {isFullscreen ? (
                <Minimize2 className="w-10 h-10" />
              ) : (
                <Maximize2 className="w-10 h-10" />
              )}
            </button>
            {/* <div className="hidden sm:flex items-center gap-4">
              <img src={neuneLogo} alt="Neune Labs Logo" className="w-10 h-10 filter brightness-0 invert" />
            </div> */}
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="absolute bg-white text-black p-2 rounded shadow-lg"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseLeave={handleContextMenuClose}
        >
          <div className="p-2">Built by AAP</div>
          {/* <div className="p-2">Video ID: {videoId}</div> */}
          <div className="p-2">Duration: {formatTime(duration)}</div>
        </div>
      )}

    </div>
  );
};

export default WayPlayer;